/**
 * Spec 30 phase B: sending a load's carrier confirmation to its subhauler.
 *
 * Two surfaces send it (the load page's "Carrier confirmation" panel and the
 * dispatch board's row menu), and both must say the same thing about the
 * same answer, so the request and its two readings live here.
 *
 * POST /loads/:id/subhauler-confirmation answers 201 with the new documents
 * row EVEN WHEN THE SEND FAILED (the row's status is then 'error' and its
 * `status_message` is Ladeway's sentence for why), so a 201 is not by itself
 * a success. Every refusal before a row exists is an RFC7807 problem
 * (ApiProblem.js) whose `title` names what is wrong and whose `detail` says
 * what to do: SUBHAULER_CONFIRMATION_NO_SUBHAULER / _NO_EMAIL,
 * OPENSIGN_NOT_CONFIGURED, DOCUMENT_SEND_CEILING_REACHED, and the demo's
 * DEMO_DISABLED. Title and detail together are the message, the same reading
 * BillingList's invoice Send gives its refusals.
 */
import { mapResponseError } from './formErrors';

export const SEND_FAILED_FALLBACK = 'The carrier confirmation could not be sent.';

/** POST the send. Resolves with the documents row; rejects with the axios error. */
export function sendCarrierConfirmation(http, loadId) {
  return http.post(`/loads/${loadId}/subhauler-confirmation`).then(res => (res && res.data) || {});
}

/**
 * The sentence for a refused send.
 *
 * @param {*} err an axios rejection
 * @returns {string}
 */
export function confirmationFailureText(err) {
  const response = err && err.response;
  const data = response && response.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const prose = [data.title, data.detail].filter(v => typeof v === 'string' && v.trim());
    if (prose.length) return prose.join(' ');
  }
  return mapResponseError(response, []).message || SEND_FAILED_FALLBACK;
}

/**
 * What to tell the dispatcher about a send that answered with a row.
 *
 * @param {object} doc the documents row POST answered with
 * @returns {{ tone: 'success'|'danger', message: string }}
 */
export function confirmationResult(doc) {
  if (!doc || doc.status === 'error') {
    return {
      tone: 'danger',
      message: (doc && doc.status_message) || SEND_FAILED_FALLBACK,
    };
  }
  return {
    tone: 'success',
    message: doc.recipient_email
      ? `Confirmation sent to ${doc.recipient_email}`
      : 'Confirmation sent',
  };
}
